import { useMutation } from 'react-query';

import { StyleFormat, DownloadFormat, DocumentDownloadOptions } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface UseChronologyDownloadProps {
  chronologyId: string;
  caseId: string | null;
}

interface DownloadWithDocumentsResponse {
  exportExists?: boolean;
  jobId?: string;
  inProgress?: boolean;
}

interface DownloadUrlResponse {
  downloadUrl: string;
}

const useChronologyDownload = ({ chronologyId, caseId }: UseChronologyDownloadProps) => {
  const { getFetchConfig } = useGetFetchConfig();

  const downloadChronology = async ({
    styleFormat,
    title,
    columns,
    style,
    downloadFormat,
    documentOptions,
    includeSourceDocuments = false,
  }: {
    styleFormat: StyleFormat;
    downloadFormat: DownloadFormat;
    title: string;
    columns?: string[];
    style?: string;
    documentOptions?: DocumentDownloadOptions;
    includeSourceDocuments?: boolean;
  }) => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        downloadFormat,
        styleFormat,
        caseId,
        title,
        columns,
        style,
        documentOptions,
      },
    });

    if (includeSourceDocuments) {
      // Start async job and return job info
      const endpoint = `${APIBaseChronos}/api/case/${caseId}/chronology/${chronologyId}/download-with-documents`;
      const response = await fetch(endpoint, fetchConfig);

      if (!response.ok) {
        throw new Error('Failed to start chronology export');
      }

      const data: DownloadWithDocumentsResponse = await response.json();
      return data;
    } else {
      // Original synchronous download
      const endpoint = `${APIBaseChronos}/api/case/${caseId}/chronology/${chronologyId}/download`;
      const downloadResult = await fetch(endpoint, fetchConfig);

      const blob = await downloadResult.blob();
      const link = document.createElement('a');
      const file = new Blob([blob], { type: 'text/plain' });
      link.href = URL.createObjectURL(file);
      link.download = `${title}.${downloadFormat}`;
      link.click();
      URL.revokeObjectURL(link.href);

      return null;
    }
  };

  const downloadChronologyWithDocuments = async (title: string): Promise<void> => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.GET,
    });

    const endpoint = `${APIBaseChronos}/api/case/${caseId}/chronology/${chronologyId}/export-download-url`;
    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) {
      throw new Error('Failed to get chronology export download URL');
    }

    const data: DownloadUrlResponse = await response.json();

    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.download = `${title} with source documents.zip`;
    link.click();
  };

  return {
    downloadMutation: useMutation(downloadChronology),
    downloadWithDocuments: downloadChronologyWithDocuments,
  };
};

export default useChronologyDownload;
